import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
// En tu proyecto real, asegúrate de que esta ruta sea correcta.
import { API_BASE_URL } from '../App';
import { useNavigate } from 'react-router-dom';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography, Select, MenuItem,
    FormControl, InputLabel, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Dialog, DialogActions, DialogContent,
    DialogTitle, Chip, Snackbar, Alert, CircularProgress, Tooltip,
    InputAdornment, Autocomplete, createFilterOptions, FilterOptionsState,
    FormControlLabel, Switch
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CampaignIcon from '@mui/icons-material/Campaign';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddCircleIcon from '@mui/icons-material/AddCircle';

// --- Type Definitions ---
interface TrafficSource { id: number; name: string; }
interface TrackerDomain { id: number; domain_url: string; }
interface Offer { id: number; name: string; url: string; cpa_network_id: number; payout: number; }
interface LandingPage { id: number; name: string; url: string; }
interface CpaNetwork { id: number; name: string; }

interface CampaignLandingPage {
    id: number;
    name: string;
    weight: number;
}

interface Campaign {
    id: number;
    name: string;
    status: 'active' | 'inactive';
    traffic_source_id: number;
    traffic_source_name?: string;
    tracker_domain_id: number;
    tracker_domain_url?: string;
    offers?: { id: number; name: string; }[];
    landing_pages?: CampaignLandingPage[];
    created_at: string;
    updated_at: string;
    use_double_meta_refresh?: boolean;
    external_campaign_id?: string;
    campaign_tracking_url?: string;
}
interface CampaignFormData {
    name: string;
    status: 'active' | 'inactive';
    traffic_source_id: number | '';
    tracker_domain_id: number | '';
    offer_ids: number[];
    landing_pages: { id: number; weight: number; }[];
    use_double_meta_refresh: boolean;
    external_campaign_id: string;
}

interface AddNewDisplayOption {
    id: -99;
    label: string;
    inputValue: string;
}

interface AddNewExtraFields {
    url?: string;
    payout?: number;
    cpa_network_id?: number;
}

// --- Initial State ---
const initialFormState: CampaignFormData = {
    name: '',
    status: 'active',
    traffic_source_id: '',
    tracker_domain_id: '',
    offer_ids: [],
    landing_pages: [],
    use_double_meta_refresh: false,
    external_campaign_id: '',
};

// --- HELPER & CHILD COMPONENTS ---

const CampaignForm = ({
    formData, setFormData, handleSubmit, editingId, resetForm, lists, onAddNew
}: {
    formData: CampaignFormData,
    setFormData: React.Dispatch<React.SetStateAction<CampaignFormData>>,
    handleSubmit: (e: React.FormEvent) => void,
    editingId: number | null,
    resetForm: () => void,
    lists: {
        trafficSources: TrafficSource[], trackerDomains: TrackerDomain[],
        landingPages: LandingPage[], offers: Offer[]
    },
    onAddNew: (entityType: 'trafficSource' | 'trackerDomain' | 'landingPage' | 'offer', value: string) => void
}) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name as string]: value }));
    };

    const handleSelectChange = (e: any) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name as string]: value }));
    };

    const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: checked }));
    };

    const handleLpWeightChange = (lpId: number, weight: string) => {
        const newWeight = parseInt(weight, 10);
        if (isNaN(newWeight) || newWeight < 0) return;

        setFormData(prev => ({
            ...prev,
            landing_pages: prev.landing_pages.map(lp => 
                lp.id === lpId ? { ...lp, weight: newWeight } : lp
            )
        }));
    };

    const filterTrafficSourceOptions = createFilterOptions<TrafficSource | AddNewDisplayOption>();
    const filterTrackerDomainOptions = createFilterOptions<TrackerDomain | AddNewDisplayOption>();
    const filterLandingPageOptions = createFilterOptions<LandingPage | AddNewDisplayOption>();
    const filterOfferOptions = createFilterOptions<Offer | AddNewDisplayOption>();

    return (
        <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
            <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                {editingId ? 'Editar Campaña' : 'Crear Nueva Campaña'}
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <Stack spacing={3}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                        <TextField name="name" label="Nombre de la Campaña" value={formData.name} onChange={handleInputChange} required fullWidth />
                        <FormControl fullWidth>
                            <InputLabel>Estado</InputLabel>
                            <Select name="status" value={formData.status} label="Estado" onChange={handleSelectChange}>
                                <MenuItem value="active">Activa</MenuItem>
                                <MenuItem value="inactive">Inactiva</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                    <TextField 
                        name="external_campaign_id" 
                        label="ID de Campaña en Fuente de Tráfico (Opcional)" 
                        value={formData.external_campaign_id} 
                        onChange={handleInputChange} 
                        fullWidth 
                        helperText="El ID que la red de tráfico le asigna a esta campaña (ej. el de RollerAds o ClickAdilla)."
                    />
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                        <Autocomplete
                            fullWidth freeSolo selectOnFocus clearOnBlur handleHomeEndKeys
                            options={lists.trafficSources}
                            value={lists.trafficSources.find(ts => ts.id === formData.traffic_source_id) || null}
                            onChange={(event, newValue: TrafficSource | AddNewDisplayOption | string | null) => {
                                if (typeof newValue === 'object' && newValue !== null && 'id' in newValue && newValue.id === -99) {
                                    onAddNew('trafficSource', (newValue as AddNewDisplayOption).inputValue);
                                } else {
                                    setFormData(prev => ({ ...prev, traffic_source_id: (newValue as TrafficSource)?.id || '' }));
                                }
                            }}
                            filterOptions={(options: (TrafficSource | AddNewDisplayOption)[], params: FilterOptionsState<TrafficSource | AddNewDisplayOption>) => {
                                const filtered = filterTrafficSourceOptions(options, params);
                                if (params.inputValue !== '' && !options.some((option) => (option as TrafficSource).name === params.inputValue)) {
                                    filtered.push({ id: -99, label: `Añadir "${params.inputValue}"`, inputValue: params.inputValue });
                                }
                                return filtered;
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                if ((option as AddNewDisplayOption).id === -99) return (option as AddNewDisplayOption).label;
                                return (option as TrafficSource).name;
                            }}
                            renderInput={(params) => <TextField {...params} label="Fuente de Tráfico" required />}
                        />
                        <Autocomplete
                            fullWidth freeSolo selectOnFocus clearOnBlur handleHomeEndKeys
                            options={lists.trackerDomains}
                            value={lists.trackerDomains.find(td => td.id === formData.tracker_domain_id) || null}
                            onChange={(event, newValue: TrackerDomain | AddNewDisplayOption | string | null) => {
                                if (typeof newValue === 'object' && newValue !== null && 'id' in newValue && newValue.id === -99) {
                                    onAddNew('trackerDomain', (newValue as AddNewDisplayOption).inputValue);
                                } else {
                                    setFormData(prev => ({ ...prev, tracker_domain_id: (newValue as TrackerDomain)?.id || '' }));
                                }
                            }}
                            filterOptions={(options: (TrackerDomain | AddNewDisplayOption)[], params: FilterOptionsState<TrackerDomain | AddNewDisplayOption>) => {
                                const filtered = filterTrackerDomainOptions(options, params);
                                if (params.inputValue !== '' && !options.some((option) => (option as TrackerDomain).domain_url === params.inputValue)) {
                                    filtered.push({ id: -99, label: `Añadir "${params.inputValue}"`, inputValue: params.inputValue });
                                }
                                return filtered;
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                if ((option as AddNewDisplayOption).id === -99) return (option as AddNewDisplayOption).label;
                                return (option as TrackerDomain).domain_url;
                            }}
                            renderInput={(params) => <TextField {...params} label="Dominio del Tracker" required />}
                        />
                    </Stack>
                    
                    <Autocomplete
                        multiple
                        fullWidth
                        options={lists.landingPages}
                        value={lists.landingPages.filter(lp => formData.landing_pages.some(selectedLp => selectedLp.id === lp.id))}
                        onChange={(event, newValue) => {
                            setFormData(prev => ({
                                ...prev,
                                landing_pages: newValue.map(lp => {
                                    const existingLp = prev.landing_pages.find(p => p.id === lp.id);
                                    return existingLp || { id: lp.id, weight: 50 };
                                })
                            }));
                        }}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => <TextField {...params} label="Landing Pages (Opcional)" placeholder="Seleccionar landing pages..." />}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...chipProps } = getTagProps({ index });
                                const lpData = formData.landing_pages.find(lp => lp.id === option.id);
                                return (
                                    <Chip
                                        key={key}
                                        label={
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="body2">{option.name}</Typography>
                                                <TextField
                                                    size="small"
                                                    variant="standard"
                                                    type="number"
                                                    label="Peso"
                                                    value={lpData?.weight || 50}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) => handleLpWeightChange(option.id, e.target.value)}
                                                    sx={{ width: '60px' }}
                                                />
                                            </Stack>
                                        }
                                        {...chipProps}
                                    />
                                );
                            })
                        }
                    />

                    <Autocomplete
                        multiple freeSolo selectOnFocus clearOnBlur handleHomeEndKeys
                        options={lists.offers}
                        value={lists.offers.filter(offer => formData.offer_ids.includes(offer.id))}
                        onChange={(event, newValue: (Offer | AddNewDisplayOption | string)[]) => {
                            const newOfferIds: number[] = [];
                            let addedNewValue: string | null = null; 

                            newValue.forEach(item => {
                                if (typeof item === 'object' && item !== null && 'id' in item) {
                                    if (item.id === -99) {
                                        addedNewValue = (item as AddNewDisplayOption).inputValue;
                                    } else {
                                        newOfferIds.push((item as Offer).id);
                                    }
                                } else if (typeof item === 'string') {
                                    addedNewValue = item;
                                }
                            });

                            if (addedNewValue) {
                                onAddNew('offer', addedNewValue);
                            }
                            setFormData(prev => ({ ...prev, offer_ids: newOfferIds }));
                        }}
                        filterOptions={(options: (Offer | AddNewDisplayOption)[], params: FilterOptionsState<Offer | AddNewDisplayOption>) => {
                            const filtered = filterOfferOptions(options, params);
                            if (params.inputValue !== '' && !options.some((option) => (option as Offer).name === params.inputValue)) {
                                filtered.push({ id: -99, label: `Añadir "${params.inputValue}"`, inputValue: params.inputValue });
                            }
                            return filtered;
                        }}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            if ((option as AddNewDisplayOption).id === -99) return (option as AddNewDisplayOption).label;
                            return (option as Offer).name;
                        }}
                        renderInput={(params) => <TextField {...params} label="Ofertas" placeholder="Buscar o añadir ofertas..." />}
                        renderTags={(value, getTagProps) => value.map((option, index) => {
                            const { key, ...chipProps } = getTagProps({ index });
                            return (
                                <Chip
                                    key={key}
                                    label={(option as Offer).name || (option as AddNewDisplayOption).label}
                                    {...chipProps}
                                />
                            );
                        })}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.use_double_meta_refresh}
                                onChange={handleSwitchChange}
                                name="use_double_meta_refresh"
                                color="primary"
                            />
                        }
                        label="Usar Double Meta Refresh (si es directo a la oferta)"
                        sx={{ mt: 1 }}
                    />

                    <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                        <Button type="submit" variant="contained">{editingId ? 'Actualizar Campaña' : 'Crear Campaña'}</Button>
                        {editingId && <Button variant="text" onClick={resetForm}>Cancelar</Button>}
                    </Stack>
                </Stack>
            </Box>
        </Paper>
    );
};

const CampaignsTable = ({ campaigns, onEdit, onDelete, onGetUrl, onToggleStatus, onToggleDoubleMeta, onPreview, onClone, onEditFunnel }: {
    campaigns: Campaign[], onEdit: (campaign: Campaign) => void, onDelete: (id: number) => void, onGetUrl: (url?: string) => void,
    onToggleStatus: (campaign: Campaign) => void, onToggleDoubleMeta: (campaign: Campaign) => void, onPreview: (id: number) => void, onClone: (campaign: Campaign) => void,
    onEditFunnel: (id: number) => void
}) => {
    const theme = useTheme();
    const headerCellStyle = { fontWeight: 'bold', backgroundColor: theme.palette.action.hover };

    if (campaigns.length === 0) {
        return <Typography sx={{ textAlign: 'center', p: 4 }} color="text.secondary">No se encontraron campañas.</Typography>
    }

    return (
        <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: '12px' }}>
            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellStyle}>ID</TableCell>
                            <TableCell sx={headerCellStyle}>Nombre</TableCell>
                            <TableCell sx={headerCellStyle}>Fuente de Tráfico</TableCell>
                            <TableCell sx={headerCellStyle}>Dominio</TableCell>
                            <TableCell align="center" sx={headerCellStyle}>Double Meta</TableCell>
                            <TableCell sx={headerCellStyle}>Estado</TableCell>
                            <TableCell align="right" sx={headerCellStyle}>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {campaigns.map((campaign) => (
                            <TableRow key={campaign.id} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{campaign.id}</TableCell>
                                <TableCell sx={{ textTransform: 'none' }}>{campaign.name}</TableCell>
                                <TableCell>{campaign.traffic_source_name || 'N/A'}</TableCell>
                                <TableCell>{campaign.tracker_domain_url || 'N/A'}</TableCell>
                                <TableCell align="center">
                                    <Tooltip title={campaign.use_double_meta_refresh ? 'Desactivar Double Meta Refresh' : 'Activar Double Meta Refresh'}>
                                        <Switch
                                            checked={!!campaign.use_double_meta_refresh}
                                            onChange={() => onToggleDoubleMeta(campaign)}
                                            size="small"
                                        />
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Tooltip title={`Cambiar a ${campaign.status === 'active' ? 'inactiva' : 'activa'}`}>
                                        <Chip label={campaign.status === 'active' ? 'Activa' : 'Inactiva'} color={campaign.status === 'active' ? 'success' : 'default'} size="small" onClick={() => onToggleStatus(campaign)} sx={{ cursor: 'pointer' }} />
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Editar Funnel (Reglas y Rutas)"><IconButton size="small" onClick={() => onEditFunnel(campaign.id)} color="secondary"><AccountTreeIcon /></IconButton></Tooltip>
                                    <Tooltip title="Previsualizar Flujo (no cuenta como visita)"><IconButton size="small" onClick={() => onPreview(campaign.id)} color="primary"><VisibilityIcon /></IconButton></Tooltip>
                                    <Tooltip title="Clonar Campaña"><IconButton size="small" onClick={() => onClone(campaign)}><ContentCopyIcon /></IconButton></Tooltip>
                                    <Tooltip title="Copiar URL de Tracking"><IconButton size="small" onClick={() => onGetUrl(campaign.campaign_tracking_url)}><LinkIcon /></IconButton></Tooltip>
                                    <Tooltip title="Editar Configuración Básica"><IconButton size="small" onClick={() => onEdit(campaign)}><EditIcon /></IconButton></Tooltip>
                                    <Tooltip title="Eliminar"><IconButton size="small" onClick={() => onDelete(campaign.id)} color="error"><DeleteIcon /></IconButton></Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

const ConfirmationDialog = ({ open, onClose, onConfirm, title, children }: {
    open: boolean, onClose: () => void, onConfirm: () => void, title: string, children: React.ReactNode
}) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent><Typography color="text.secondary">{children}</Typography></DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button onClick={onConfirm} color="error" variant="contained">Confirmar Eliminación</Button>
        </DialogActions>
    </Dialog>
);

// --- MAIN PAGE COMPONENT ---
const CampaignsPage: React.FC = () => {
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [trafficSources, setTrafficSources] = useState<TrafficSource[]>([]);
    const [trackerDomains, setTrackerDomains] = useState<TrackerDomain[]>([]);
    const [offers, setOffers] = useState<Offer[]>([]);
    const [landingPages, setLandingPages] = useState<LandingPage[]>([]);
    const [cpaNetworks, setCpaNetworks] = useState<CpaNetwork[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState<CampaignFormData>(initialFormState);
    const [deleteDialog, setDeleteDialog] = useState<{ open: boolean, id: number | null }>({ open: false, id: null });

    const [addDialog, setAddDialog] = useState<{
        open: boolean;
        type: 'trafficSource' | 'trackerDomain' | 'landingPage' | 'offer' | '';
        title: string;
        initialValue: string;
        extraFields: AddNewExtraFields;
    }>({ open: false, type: '', title: '', initialValue: '', extraFields: {} });

    const fetchData = useCallback(async (selects?: { [key: string]: any }) => {
        setError(null);
        if (campaigns.length === 0) setIsLoading(true);
        try {
            const [campaignsRes, tsRes, tdRes, offersRes, lpRes, cpaNetworksRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/campaigns`),
                axios.get(`${API_BASE_URL}/traffic-sources`),
                axios.get(`${API_BASE_URL}/tracker-domains`),
                axios.get(`${API_BASE_URL}/offers`),
                axios.get(`${API_BASE_URL}/landing-pages`),
                axios.get(`${API_BASE_URL}/cpa-networks`),
            ]);
            setCampaigns(campaignsRes.data);
            setTrafficSources(tsRes.data);
            setTrackerDomains(tdRes.data);
            setOffers(offersRes.data);
            setLandingPages(lpRes.data);
            setCpaNetworks(cpaNetworksRes.data);

            if (selects) {
                setFormData(prev => ({ ...prev, ...selects }));
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Error al cargar los datos.');
        } finally {
            setIsLoading(false);
        }
    }, [campaigns.length]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    // =========================================================================
    // NUEVO: Memoización para combinar los datos de la campaña con sus detalles
    // =========================================================================
    const campaignsWithDetails = useMemo(() => {
        // Evita procesar si los datos aún no están cargados
        if (!campaigns.length || !trackerDomains.length || !trafficSources.length) {
            return [];
        }

        return campaigns.map(campaign => {
            const trafficSource = trafficSources.find(ts => ts.id === campaign.traffic_source_id);
            const trackerDomain = trackerDomains.find(td => td.id === campaign.tracker_domain_id);

            return {
                ...campaign,
                // Asigna los nombres y URLs correspondientes
                traffic_source_name: trafficSource ? trafficSource.name : 'N/A',
                tracker_domain_url: trackerDomain ? trackerDomain.domain_url : 'N/A',
            };
        });
    }, [campaigns, trafficSources, trackerDomains]); // Se recalcula si cambia alguno de estos arreglos


    const resetForm = () => {
        setEditingId(null);
        setFormData(initialFormState);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        const campaignData = {
            ...formData,
            traffic_source_id: Number(formData.traffic_source_id),
            tracker_domain_id: Number(formData.tracker_domain_id),
        };
        const endpoint = editingId ? `/campaigns/${editingId}` : '/campaigns';
        const method = editingId ? 'PUT' : 'POST';

        try {
            await axios({ method, url: `${API_BASE_URL}${endpoint}`, data: campaignData });
            setNotification({ type: 'success', message: `Campaña ${editingId ? 'actualizada' : 'creada'} con éxito!` });
            resetForm();
            await fetchData();
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al guardar la campaña.' });
        }
    };

    const startEditing = (campaign: Campaign) => {
        setEditingId(campaign.id);
        setFormData({
            name: campaign.name,
            status: campaign.status,
            traffic_source_id: campaign.traffic_source_id,
            tracker_domain_id: campaign.tracker_domain_id,
            offer_ids: campaign.offers?.map(o => o.id) || [],
            landing_pages: campaign.landing_pages?.map(lp => ({ id: lp.id, weight: lp.weight })) || [],
            use_double_meta_refresh: campaign.use_double_meta_refresh || false,
            external_campaign_id: campaign.external_campaign_id || '',
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleClone = (campaignToClone: Campaign) => {
        setEditingId(null);
        setFormData({
            name: `${campaignToClone.name} (Copia)`,
            status: campaignToClone.status,
            traffic_source_id: campaignToClone.traffic_source_id,
            tracker_domain_id: campaignToClone.tracker_domain_id,
            offer_ids: campaignToClone.offers?.map(o => o.id) || [],
            landing_pages: campaignToClone.landing_pages?.map(lp => ({ id: lp.id, weight: lp.weight })) || [],
            use_double_meta_refresh: campaignToClone.use_double_meta_refresh || false,
            external_campaign_id: campaignToClone.external_campaign_id || '',
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setNotification({ type: 'success', message: `Clonando '${campaignToClone.name}'. Revisa los detalles y crea la nueva campaña.` });
    };

    const handleDelete = async () => {
        if (!deleteDialog.id) return;
        try {
            await axios.delete(`${API_BASE_URL}/campaigns/${deleteDialog.id}`);
            setNotification({ type: 'success', message: 'Campaña eliminada con éxito.' });
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al eliminar la campaña.' });
        } finally {
            setDeleteDialog({ open: false, id: null });
            await fetchData();
        }
    };

    const handleToggleStatus = async (campaign: Campaign) => {
        const newStatus = campaign.status === 'active' ? 'inactive' : 'active';
        try {
            await axios.put(`${API_BASE_URL}/campaigns/${campaign.id}`, { status: newStatus });
            // La actualización se reflejará automáticamente cuando 'fetchData' se ejecute, 
            // pero hacemos un cambio optimista para una UI más rápida.
            setCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, status: newStatus } : c));
            setNotification({ type: 'success', message: `Estado de la campaña "${campaign.name}" actualizado.` });
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al actualizar el estado.' });
        }
    };

    const handleToggleDoubleMeta = async (campaign: Campaign) => {
        const newValue = !campaign.use_double_meta_refresh;
        try {
            setCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, use_double_meta_refresh: newValue } : c));
            await axios.put(`${API_BASE_URL}/campaigns/${campaign.id}`, { use_double_meta_refresh: newValue });
            setNotification({ type: 'success', message: `Double Meta Refresh para "${campaign.name}" ${newValue ? 'activado' : 'desactivado'}.` });
        } catch (err: any) {
            setCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, use_double_meta_refresh: !newValue } : c));
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al actualizar Double Meta Refresh.' });
        }
    };

    const handleGetUrl = (url: string | undefined) => {
        if (url) {
            navigator.clipboard.writeText(url).then(() => {
                setNotification({ type: 'success', message: '¡URL copiada al portapapeles!' });
            }).catch(() => {
                setNotification({ type: 'error', message: 'Error al copiar la URL.' });
            });
        }
    };

    const handlePreview = async (campaignId: number) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/campaigns/${campaignId}/generate-url`);
            let trackingUrl = response.data.trackingUrl;
            
            if (trackingUrl) {
                const finalPreviewUrl = trackingUrl.includes('?') ? `${trackingUrl}&preview=true` : `${trackingUrl}?preview=true`;
                window.open(finalPreviewUrl, '_blank', 'noopener,noreferrer');
            } else {
                setNotification({ type: 'error', message: 'La URL de previsualización generada está vacía.' });
            }
        } catch (err: any) {
            setNotification({ type: 'error', message: `Error al previsualizar: ${err.response?.data?.message || err.message}` });
        }
    };

    const handleAddNewEntity = async () => { /* ... */ };
    const openAddDialog = (type: 'trafficSource' | 'trackerDomain' | 'landingPage' | 'offer', value: string) => { /* ... */ };
    const copyToClipboard = async (url: string) => { /* ... */ };

    // =========================================================================
    // MODIFICADO: Filtrar sobre el arreglo que ya tiene los detalles combinados
    // =========================================================================
    const filteredCampaigns = useMemo(() => campaignsWithDetails.filter(campaign => {
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        if (!lowerCaseSearchTerm) return true;
        
        // Búsqueda mejorada en varios campos
        return (
            String(campaign.id).includes(lowerCaseSearchTerm) ||
            campaign.name.toLowerCase().includes(lowerCaseSearchTerm) ||
            (campaign.traffic_source_name && campaign.traffic_source_name.toLowerCase().includes(lowerCaseSearchTerm)) ||
            (campaign.tracker_domain_url && campaign.tracker_domain_url.toLowerCase().includes(lowerCaseSearchTerm))
        );
    }), [campaignsWithDetails, searchTerm]); // La dependencia ahora es el arreglo combinado

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    if (error && campaigns.length === 0) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Snackbar open={!!notification} autoHideDuration={6000} onClose={() => setNotification(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>{notification?.message}</Alert>
            </Snackbar>

            <Dialog open={addDialog.open} onClose={() => setAddDialog(prev => ({ ...prev, open: false }))} fullWidth maxWidth="sm">
                {/* ... */}
            </Dialog>

            <ConfirmationDialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })} onConfirm={handleDelete} title="Confirmar Eliminación">
                ¿Estás seguro de que quieres eliminar esta campaña? Esta acción es irreversible.
            </ConfirmationDialog>

            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                <CampaignIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Gestión de Campañas
            </Typography>

            <CampaignForm
                formData={formData}
                setFormData={setFormData}
                handleSubmit={handleSubmit}
                editingId={editingId}
                resetForm={resetForm}
                lists={{ trafficSources, trackerDomains, landingPages, offers }}
                onAddNew={openAddDialog}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 5, mb: 2 }}>
                <Typography variant="h5" component="h2" color="text.primary">Campañas Registradas</Typography>
                <TextField size="small" variant="outlined" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }} />
            </Stack>

            <CampaignsTable
                campaigns={filteredCampaigns} // MODIFICADO: Se pasa el arreglo filtrado y combinado
                onEdit={startEditing}
                onDelete={(id) => setDeleteDialog({ open: true, id })}
                onGetUrl={handleGetUrl}
                onToggleStatus={handleToggleStatus}
                onToggleDoubleMeta={handleToggleDoubleMeta}
                onPreview={handlePreview}
                onClone={handleClone}
                onEditFunnel={(id) => navigate(`/campaigns/${id}/editor`)}
            />
        </Container>
    );
};

export default CampaignsPage;